import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { AppDispatch, getCurrentDesignName, setCurrentDesignName } from "../../store";
import { loadDesignState, startProblem } from "../../store/game/gameSlice";
import { deleteProblemAttempt, loadProblemAttemptState } from "../../store/problemAttempts";
import { trackEvent, GA_EVENTS } from "../../utils/analytics";

/**
 * Starting a problem replaces the scratchpad. A named design or a problem attempt is
 * already persisted, so only a plain unnamed scratchpad with blocks counts as unsaved work.
 */
export const useHasUnsavedScratchpad = (): boolean => {
  const blockCount = useAppSelector((state) => Object.keys(state.game.blocks).length);
  const activeProblemId = useAppSelector((state) => state.game.activeProblemId);
  return getCurrentDesignName() === null && !activeProblemId && blockCount > 1;
};

/**
 * Load a problem into the store: its saved attempt if there is one, otherwise a fresh
 * start (which the store subscription then saves as the new attempt).
 * Returns true when an existing attempt was resumed.
 */
export const openProblem = (dispatch: AppDispatch, problemId: string): boolean => {
  const saved = loadProblemAttemptState(problemId);
  if (saved) {
    dispatch(loadDesignState(saved));
    return true;
  }
  dispatch(startProblem(problemId));
  return false;
};

export const useStartProblem = () => {
  const dispatch = useAppDispatch();
  return useCallback(
    (problemId: string) => {
      // Detach from any named design so the problem attempt lands in the scratchpad
      setCurrentDesignName(null);
      const resumed = openProblem(dispatch, problemId);
      trackEvent(resumed ? GA_EVENTS.PROBLEM_RESUMED : GA_EVENTS.PROBLEM_STARTED, { problem_id: problemId });
    },
    [dispatch],
  );
};

/** Discard a problem's saved attempt and start it fresh. */
export const useRestartProblem = () => {
  const dispatch = useAppDispatch();
  return useCallback(
    (problemId: string) => {
      setCurrentDesignName(null);
      deleteProblemAttempt(problemId);
      dispatch(startProblem(problemId));
      trackEvent(GA_EVENTS.PROBLEM_RESTARTED, { problem_id: problemId });
    },
    [dispatch],
  );
};
